import { LeadContractAction } from "./LeadContractAction";
import { InlineLeadReview } from "./reviews/InlineLeadReview";
import { Dropdown } from "react-bootstrap";
import { MoreHorizontal } from "lucide-react";
import { PartnerName, PartnerReviewButton } from "components/reviews/PartnerReview";
import { Lead } from '../types/lead';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MapPin, Calendar, DollarSign, Clock, User, Star, TrendingUp, X, CheckCircle, PlayCircle, Images } from 'lucide-react';
import { Avatar, AvatarFallback } from './ui/avatar';
import { motion } from 'motion/react';
import { useState } from 'react';
import { MediaViewer } from './MediaViewer';
import { grayColor } from 'Common/commaon';
import { useTranslation } from 'react-i18next';

interface LeadCardProps {
  lead: any;
  onViewDetails: (leadId: string) => void;
  onApplyLead?: (leadId: string) => void;
  onAcceptLead?: (leadId: string) => void;
  onDeclineLead?: (leadId: string) => void;
  showActionButtons?: boolean;
  isRequestedLead?: boolean;
  isAcceptedLead?: boolean;
  onUpdateStatus?: () => void;
  compact?: boolean;
}

export function LeadCard({ lead, onViewDetails, onApplyLead, onAcceptLead, onDeclineLead, showActionButtons = false, isRequestedLead = false,  isAcceptedLead = false,
  onUpdateStatus, compact = false }: LeadCardProps) {
  const [hasInlineReview, setHasInlineReview] = useState(false);
  const [isMediaViewerOpen, setIsMediaViewerOpen] = useState(false);

  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'open':
        return {
          badge: 'bg-green-500 text-white hover:bg-green-600',
          accent: 'bg-green-500 to-emerald-500'
        };
        case 'completed':
          return {
            badge: 'bg-green-500 text-white hover:bg-green-600',
            accent: 'bg-green-500 to-emerald-500'
          };
          case 'dispute':
            return {
              badge: 'bg-red-500 text-white hover:bg-red-600',
              accent: 'bg-red-500 to-emerald-500'
            };
            case 'accepted':
              return {
                badge: 'bg-blue-600 text-white hover:bg-blue-700',
                accent: 'bg-blue-500'
              };
      case 'requested':
        return {
          badge: 'bg-blue-500 text-white hover:bg-blue-600',
          accent: 'bg-blue-500 to-cyan-500'
        };
      default:
        return {
          badge: 'bg-gray-500 text-white',
          accent: 'bg-gray-500'
        };
    }
  };

  const getCategoryStyles = (category: string) => {
    const styles: Record<string, { bg: string; text: string; icon: string }> = {
      plumbing: { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'bg-blue-500' },
      electrical: { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: 'bg-yellow-500' },
      carpentry: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'bg-amber-500' },
      hvac: { bg: 'bg-cyan-50', text: 'text-cyan-700', icon: 'bg-cyan-500' },
      painting: { bg: 'bg-purple-50', text: 'text-purple-700', icon: 'bg-purple-500' },
      roofing: { bg: 'bg-slate-50', text: 'text-slate-700', icon: 'bg-slate-500' },
      landscaping: { bg: 'bg-green-50', text: 'text-green-700', icon: 'bg-green-500' },
      general: { bg: 'bg-gray-50', text: 'text-gray-700', icon: 'bg-gray-500' }
    };
    return styles[category] || styles.general;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
  
    const date = new Date(dateString);   // Automatically converts to local time
    const now = new Date();
  
    let diffMs = now.getTime() - date.getTime();
    if (diffMs < 0) diffMs = 0; // prevent negative values
  
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);
  
    if (diffSec < 30) return "Just now";
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
  
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  const statusStyles = getStatusStyles(lead.status);
  const categoryStyles = getCategoryStyles(lead.category);

  // Get media items (prefer media array, fallback to images)
  const mediaItems = lead.media || (lead.images?.map((url:any) => ({ type: 'image' as const, url })) || []);
  const hasMedia = mediaItems.length > 0;
  const hasVideo = mediaItems.some((item:any) => item.type === 'video');
  const { t } = useTranslation();

  if (isAcceptedLead) {
    const homeownerName = lead.homeowner?.name || "Homeowner";
    const serviceNames = Array.isArray(lead.lead_sub_cat) ? lead.lead_sub_cat.map((service: { name?: string }) => service.name).filter(Boolean) : [];
    return <>
      <article className={`accepted-lead-card${hasInlineReview ? " accepted-lead-card--review" : ""}`}>
        <button type="button" className="accepted-lead-image" onClick={() => hasMedia ? setIsMediaViewerOpen(true) : onViewDetails(lead.id)} aria-label={hasMedia ? `View photos for ${lead.title}` : `View ${lead.title}`}>
          {lead.images?.[0] ? <img src={lead.images[0]} alt="" /> : <Images size={28} aria-hidden="true" />}
          {hasMedia && <span><Images size={13} aria-hidden="true" /> {mediaItems.length} {mediaItems.length === 1 ? "photo" : "items"}</span>}
        </button>
        <div className="accepted-lead-body">
          <div className="accepted-lead-top"><span className="accepted-lead-status"><CheckCircle size={12} aria-hidden="true" /> Accepted</span><span className="accepted-lead-reference">Lead #{lead.id}</span><div className="accepted-lead-budget"><strong>${Number(lead.budget || 0).toLocaleString()}</strong><span>Budget</span></div></div>
          <button type="button" className="accepted-lead-title" onClick={() => onViewDetails(lead.id)}>{lead.title}</button>
          {lead.description && <p className="accepted-lead-description">{lead.description}</p>}
          <div className="accepted-lead-meta"><span><MapPin size={14} aria-hidden="true" />{[lead.location?.city, lead.location?.state].filter(Boolean).join(", ") || "Location not provided"}</span><span><Calendar size={14} aria-hidden="true" />{lead.acceptedDate ? "Accepted " : "Posted "}{formatDate(lead.acceptedDate || lead.postedDate)}</span>{serviceNames.length > 0 && <span className="accepted-lead-service">{serviceNames.slice(0,2).join(" · ")}{serviceNames.length > 2 ? ` +${serviceNames.length - 2}` : ""}</span>}</div>
          <footer className="accepted-lead-footer">
            <div className="accepted-lead-person"><PartnerName id={lead.homeowner?.id}><span className="accepted-lead-avatar" aria-hidden="true">{lead.homeowner?.pic ? <img src={lead.homeowner.pic} alt="" /> : getInitials(homeownerName)}</span><span className="accepted-lead-person-copy"><span className="accepted-lead-person-label">Homeowner</span><span className="accepted-lead-person-name">{homeownerName}</span></span></PartnerName></div>
            <div className="accepted-lead-actions"><LeadContractAction leadId={lead.id} /><button type="button" className="accepted-lead-button accepted-lead-button-primary" onClick={() => onViewDetails(lead.id)}>View details</button>{(onUpdateStatus || onDeclineLead) && <Dropdown><Dropdown.Toggle variant="link" className="accepted-lead-more" aria-label={`More actions for ${lead.title}`}><MoreHorizontal size={19} aria-hidden="true" /></Dropdown.Toggle><Dropdown.Menu align="end">{onUpdateStatus && <Dropdown.Item onClick={onUpdateStatus}>Update work status</Dropdown.Item>}{onDeclineLead && <><Dropdown.Divider /><Dropdown.Item className="text-danger" onClick={() => onDeclineLead(lead.id)}>Decline lead</Dropdown.Item></>}</Dropdown.Menu></Dropdown>}</div>
          </footer>
        </div>
        <InlineLeadReview receiverId={lead.homeowner?.id} sourceId={lead.id} name={homeownerName} onAvailabilityChange={setHasInlineReview} />
      </article>
      {isMediaViewerOpen && <MediaViewer mediaItems={mediaItems} onClose={() => setIsMediaViewerOpen(false)} />}
    </>;
  }

  if (compact) {
    const rawStatus = String(lead.status || '').toLowerCase();
    const statusLabel = ['accepted', 'open', 'requested', 'pending', 'in_progress'].includes(rawStatus)
      ? 'In progress'
      : rawStatus === 'dispute' || rawStatus === 'disputed'
        ? 'Disputed'
        : rawStatus
          ? rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1)
          : 'Other';

    return (
      <>
        <motion.article
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22 }}
          className="lead-card lead-card--compact group grid overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md lg:grid-cols-[210px_minmax(0,1fr)_170px]"
        >
          <button
            type="button"
            className="relative h-44 overflow-hidden border-0 bg-gray-100 p-0 text-left lg:h-full lg:min-h-[210px]"
            onClick={() => hasMedia && setIsMediaViewerOpen(true)}
            disabled={!hasMedia}
            aria-label={hasMedia ? `View media for ${lead.title}` : undefined}
          >
            {lead.images?.[0] ? (
              <img src={lead.images[0]} alt="" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
            ) : (
              <span className="grid h-full place-items-center text-gray-400"><Images className="size-8" /></span>
            )}
            <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusStyles.badge}`}>
              {statusLabel}
            </span>
          </button>

          <div className="min-w-0 p-5">
            <h3 className="mb-1 text-xl font-semibold leading-tight text-gray-900">{lead.title}</h3>
            <p className="mb-4 line-clamp-1 text-sm text-gray-600">{lead.description}</p>

            <div className="mb-4 flex flex-wrap gap-x-6 gap-y-3 text-sm text-gray-600">
              <span className="flex items-center gap-2"><MapPin className="size-4 text-blue-500" />{lead.location?.city}, {lead.location?.state}</span>
              <span className="flex items-center gap-2"><Clock className="size-4 text-purple-500" />Posted {formatDate(lead.postedDate)}</span>
              {lead.acceptedDate && <span className="flex items-center gap-2"><Calendar className="size-4 text-green-500" />Accepted {formatDate(lead.acceptedDate)}</span>}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-800">
                <Avatar className="size-8 border border-white">
                  {lead.homeowner?.pic ? <img src={lead.homeowner.pic} alt="" className="h-full w-full object-cover" /> : <AvatarFallback>{getInitials(lead.homeowner?.name || 'User')}</AvatarFallback>}
                </Avatar>
                <PartnerName id={lead.homeowner?.id}>{lead.homeowner?.name || 'Homeowner'}</PartnerName>
              </span>
              {lead.lead_sub_cat?.slice(0, 2).map((service:any) => (
                <span key={service.id || service.name} className="rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700">
                  {String(service.name || '').replace(/\b\w/g, (letter) => letter.toUpperCase())}
                </span>
              ))}
            </div>
          </div>

          <div className="lead-card-compact-actions flex items-center justify-between gap-4 border-t border-gray-100 p-5 lg:flex-col lg:items-stretch lg:justify-center lg:border-l lg:border-t-0">
            <div>
              <div className="text-xs text-gray-500">Budget</div>
              <div className="mt-1 text-xl font-semibold text-gray-900">${Number(lead.budget || 0).toLocaleString()}</div>
            </div>
            <Button onClick={() => onViewDetails(lead.id)} className="min-w-[120px] whitespace-nowrap bg-blue-600 text-white hover:bg-blue-700">View details</Button>
            {isAcceptedLead && <PartnerReviewButton receiverId={lead.homeowner?.id} sourceType="job" sourceId={lead.id} />}
          </div>
        </motion.article>

        {isMediaViewerOpen && <MediaViewer mediaItems={mediaItems} onClose={() => setIsMediaViewerOpen(false)} />}
      </>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`lead-card group relative bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 ${compact ? "lead-card--compact" : ""}`}
      >

        {/* Accent Bar */}
        {!compact && <div className={`absolute top-0 left-0 right-0 h-1 ${statusStyles.accent}`} />}
        
        <div className="flex flex-col lg:flex-row">
          {/* Image Section */}
          <div 
            className={`relative h-52 flex-shrink-0 overflow-hidden cursor-pointer ${compact ? "lg:w-[260px] lg:h-auto" : "lg:w-[340px] lg:h-auto"}`}
            onClick={() => hasMedia && setIsMediaViewerOpen(true)}
          >
            <img
              src={lead.images?.[0]}
              alt={lead.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            {/* Click to View Overlay */}
            {hasMedia && (
  <div
    style={{
      position: "absolute",
      top: "10px",
      right: "10px",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: 0,
      transition: "opacity 0.3s",
      backgroundColor: "rgba(0,0,0,0.4)",
      backdropFilter: "blur(6px)",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
    onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
  >
    {hasVideo ? (
      <div style={{ color: "white", textAlign: "center" }}>
        <PlayCircle style={{ width: "64px", height: "64px", margin: "0 auto 8px" }} />
        <div>Click to view media</div>
      </div>
    ) : (
      <div style={{ color: "white", textAlign: "center" }}>
        <Images style={{ width: "64px", height: "64px", margin: "0 auto 8px" }} />
        <div>Click to view images</div>
      </div>
    )}
  </div>
)}


            {/* Media Counter Badge */}
            {mediaItems.length > 1 && (
              <div className="absolute top-4 right-4">
                <Badge className="bg-black/60 text-white backdrop-blur-sm border-0 shadow-lg">
                  <Images className="size-3 mr-1" />
                  {mediaItems.length}
                </Badge>
              </div>
            )}
            
            {/* Floating Badges on Image */}
            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
           
              <Badge className={`${statusStyles.badge} border-0 shadow-lg`}>
                <TrendingUp className="size-3 mr-1" />
                {lead.status.toUpperCase()}
              </Badge>
            </div>

            {/* Budget Badge on Image */}
            <div className="absolute bottom-4 left-4">
              <div className="lead-card-budget bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg">
                <div className="flex items-center gap-2">
                  <DollarSign className="size-5 text-green-600" />
                  <div>
                    <div className="text-xs text-gray-600">Budget</div>
                    <div className="font-semibold text-green-700">
                      ${ Number(lead.budget).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className={`flex-1 flex flex-col ${compact ? "p-5 lg:p-6" : "p-6 lg:p-8"}`}>
            {/* Header */}
            <div className="flex-1">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <h3 className="mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                    {lead.title}
                  </h3>
                  <div className="text-gray-600 line-clamp-2 mb-4">
                    {lead.description}
                  </div>
                </div>
              </div>

              {/* Info Grid */}
              <div className={`grid grid-cols-2 gap-4 ${compact ? "mb-4" : "mb-6"}`}>
                <div className="flex items-center gap-2 text-gray-600">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <MapPin className="size-4 text-blue-600" />
                  </div>
                  <div className="overflow-hidden">
                    <div className="text-xs text-gray-500">Location</div>
                    <div className="truncate">{lead.location.city}, {lead.location.state}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-gray-600">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Clock className="size-4 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Posted</div>
                    <div>{formatDate(lead.postedDate)}</div>
                    {}
                  </div>
                </div>

               

                {lead.acceptedDate && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <Calendar className="size-4 text-green-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Accepted</div>
                      <div>{formatDate(lead.acceptedDate)}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Homeowner Info */}
              <div className={`lead-card-customer flex items-center gap-3 bg-gray-50 rounded-lg ${compact ? "w-fit min-w-[240px] max-w-full p-3 mb-4" : "p-4 mb-6"}`}>
              <Avatar
  style={{
    width: "3rem", // size-12 = 48px
    height: "3rem",
    border: "2px solid white",
    borderRadius: "9999px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)", // shadow-sm
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
  {lead.homeowner.pic ?  <img
              src={lead.homeowner.pic}
              alt={lead.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            /> :  <AvatarFallback
            style={{
              width: "100%",
              height: "100%",
              background: "linear-gradient(to bottom right, #3b82f6, #9333ea)", // from-blue-500 to-purple-600
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              fontSize: "1rem",
              textTransform: "uppercase",
              borderRadius: "9999px",
            }}
          >
            {getInitials(lead.homeowner.name)}
          </AvatarFallback>}

</Avatar>

                <div className="flex-1 min-w-0">
                  <div className="truncate"><PartnerName id={lead.homeowner.id}>{lead.homeowner.name}</PartnerName></div>
                  {/* <div className="flex items-center gap-3 text-gray-600">
                 {lead.homeowner.completedProjects >0&& <span className="text-sm">{lead.homeowner.completedProjects} projects completed</span>}
                   
                  </div> */}
                </div>
              </div>
            </div>

            {lead.lead_sub_cat && lead.lead_sub_cat.length > 0 && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {lead.lead_sub_cat.slice(0, 3).map((service:any) => (
                  <span
                    key={service.id}
                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200"
                  >
                    {service.name}
                  </span>
                ))}
                {lead.lead_sub_cat.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    +{lead.lead_sub_cat.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
 
            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                onClick={() => onViewDetails(lead.id)} 
                variant="outline"
               
                className="flex-1 border-gray-300 border hover:border-blue-500 hover:bg-blue-50 bg-transparent hover:text-blue-700 transition-all"
              >
                View Details
              </Button>
              {showActionButtons && (
                <>
                  {onApplyLead && (
                   <Button
                   onClick={() => onApplyLead(lead.id)}
                   style={{
                     flex: 1,
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     background: "linear-gradient(to right, #2563eb, #1d4ed8)", // from-blue-600 to-blue-700
                     color: "white",
                     boxShadow: "0 10px 15px -3px rgba(59,130,246,0.3)", // shadow-blue-500/30
                     border: "none",
                     borderRadius: "8px",
                     padding: "10px 20px",
                     fontWeight: 500,
                     cursor: "pointer",
                     transition: "all 0.3s ease",
                   }}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.background = "linear-gradient(to right, #1d4ed8, #1e40af)"; // hover:from-blue-700 hover:to-blue-800
                     e.currentTarget.style.boxShadow = "0 12px 20px -3px rgba(59,130,246,0.4)"; // hover:shadow-blue-500/40
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.background = "linear-gradient(to right, #2563eb, #1d4ed8)";
                     e.currentTarget.style.boxShadow = "0 10px 15px -3px rgba(59,130,246,0.3)";
                   }}
                 >
                   <TrendingUp style={{ width: 16, height: 16, marginRight: 8 }} />
                   Accept Lead
                 </Button>
                 
                  )}
                  {onAcceptLead && (
                  <Button
                  onClick={() => onAcceptLead(lead.id)}
                  style={{
                    flex: 1,
                    background: "linear-gradient(to right, #16a34a, #15803d)", // from-green-600 to-green-700
                    boxShadow: "0 10px 15px -3px rgba(34,197,94,0.3)", // shadow-green-500/30
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "8px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "linear-gradient(to right, #15803d, #166534)"; // hover darker
                    e.currentTarget.style.boxShadow = "0 12px 20px -3px rgba(34,197,94,0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "linear-gradient(to right, #16a34a, #15803d)";
                    e.currentTarget.style.boxShadow = "0 10px 15px -3px rgba(34,197,94,0.3)";
                  }}
                >
                      <CheckCircle className="size-4 mr-2" />
                      Accept Lead
                    </Button>
                  )}
                  {onDeclineLead &&!isAcceptedLead && (
                    <Button 
                      onClick={() => onDeclineLead(lead.id)}
                      variant="ghost"
                      className="bg-red-50   text-red-600 transition-all"
                    >
                      <X className="size-4" />
                      Decline
                    </Button>
                  )}
                     {isAcceptedLead && <PartnerReviewButton receiverId={lead.homeowner?.id} sourceType="job" sourceId={lead.id} />}
                  {isAcceptedLead && onUpdateStatus && onDeclineLead && (
                      <>
                  <Button
  onClick={onUpdateStatus}
  style={{
    flex: 1,
    backgroundImage: "linear-gradient(to right, #16a34a, #15803d)", // green-600 → green-700
    boxShadow: "0 0 15px rgba(34,197,94,0.3)",
    transition: "all 0.2s ease-in-out",
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.backgroundImage =
      "linear-gradient(to right, #15803d, #166534)"; // hover: from-green-700 to-green-800
    e.currentTarget.style.boxShadow = "0 0 18px rgba(34,197,94,0.4)";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.backgroundImage =
      "linear-gradient(to right, #16a34a, #15803d)";
    e.currentTarget.style.boxShadow = "0 0 15px rgba(34,197,94,0.3)";
  }}
>
                          <CheckCircle className="size-4 mr-2" />
                          Update Status
                        </Button>
                        <Button
  onClick={() => onDeclineLead(lead.id)}
  variant="ghost"
  style={{
    transition: "all 0.2s ease-in-out",
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.backgroundColor = "#b91c1c"; // red-700
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.backgroundColor = ""; // back to default destructive color
  }}
  className="bg-red-50   text-red-600 transition-all"
>
                          <X className="size-4 mr-2" />
                          Decline
                        </Button>
                      </>
                    )}
                </>
              )}
            </div>

            {showActionButtons && (lead.status === 'open' ||lead.status === 'requested')&& (onAcceptLead ||onApplyLead)  && (
                <div className="lead-card-fee flex mt-3 items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <DollarSign className="size-4 text-amber-600 flex-shrink-0" />
                  <div className="text-xs text-amber-800">
                  {t("Tradesmens Will Charge")}  ${lead?.tradesmens_commission}
                  </div>
                </div>
              )}
          </div>
        </div>
      </motion.div>

      {/* Media Viewer */}
      {isMediaViewerOpen && (
        <MediaViewer
          mediaItems={mediaItems}
          onClose={() => setIsMediaViewerOpen(false)}
        />
      )}
    </>
  );
}
